import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { FiAlertTriangle, FiCheckCircle, FiClock, FiShield, FiX } from "react-icons/fi";
import { getMintSpaceKyb } from "../../api/mintSpace";
import { useNotifications } from "../../context/NotificationContext";
import BusinessVerificationPanel from "./BusinessVerificationPanel";
import KycStatusBadge from "./KycStatusBadge";

/**
 * A business's verification (KYB), shown to its owner on the business itself — a badge
 * under its name that opens the form in a window, rather than a page to go looking for.
 *
 * The status is read once per business and re-read when a decision about it arrives as
 * a notification. A notification's link (?verification=1) opens the window.
 */

/**
 * Where this business's verification stands, and whether its window is open.
 * @returns {{ status: string|null, setStatus: Function, open: boolean, setOpen: Function }}
 */
export function useBusinessVerification(mintSpaceId, enabled) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { notifications } = useNotifications();

  // The newest decision about this business; when one arrives, the status is re-read.
  const latestDecision = useMemo(
    () =>
      notifications.find(
        (n) => String(n.type || "").startsWith("kyb_") && n.meta?.mintSpaceId === String(mintSpaceId)
      )?._id || null,
    [notifications, mintSpaceId]
  );

  useEffect(() => {
    setStatus(null);
    if (!enabled || !mintSpaceId) return undefined;
    let alive = true;
    getMintSpaceKyb(mintSpaceId)
      .then((res) => alive && setStatus(res?.data?.status || "none"))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enabled, mintSpaceId, latestDecision]);

  // Opened from a notification: ?verification=1, or ?section=verification from before.
  const linked =
    searchParams.get("verification") === "1" || searchParams.get("section") === "verification";
  useEffect(() => {
    if (!enabled || !linked) return;
    setOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("verification");
        next.delete("section");
        return next;
      },
      { replace: true }
    );
  }, [enabled, linked, setSearchParams]);

  return { status, setStatus, open, setOpen };
}

const LOOK = {
  none: { icon: FiShield, tone: "bg-accent-tinted text-accent-text ring-accent-default" },
  pending: { icon: FiClock, tone: "bg-status-warning-bg text-status-warning ring-amber-500/30" },
  approved: { icon: FiCheckCircle, tone: "bg-status-success-bg text-status-success ring-emerald-500/30" },
  declined: { icon: FiAlertTriangle, tone: "bg-status-danger-bg text-status-danger ring-rose-500/30" },
};

/** The status as a small pill; nothing until it's known. */
export function BusinessVerificationBadge({ status, onClick, t, className = "" }) {
  if (!status) return null;
  const { icon: Icon, tone } = LOOK[status] || LOOK.none;
  return (
    <button
      type="button"
      onClick={onClick}
      title={t("kyc.badge.hint")}
      className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-caption-1-semibold ring-1 ring-inset transition hover:brightness-110 ${tone} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{t(`kyc.badge.${LOOK[status] ? status : "none"}`)}</span>
    </button>
  );
}

function Window({ onClose, isDark, t, mintSpaceId, status, onStatusChange, setToast }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCloseRef.current?.();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[300] flex items-start justify-center overflow-y-auto bg-overlay-scrim p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("kyc.business.title")}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`my-6 w-full max-w-3xl rounded-radius-lg border shadow-overlay ${
          "border-separator-default bg-bg-raised"
        }`}
      >
        <div
          className={`flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6 ${
            "border-separator-default"
          }`}
        >
          <div className="min-w-0">
            <h2 className={`text-title-3 text-label-primary`}>
              {t("kyc.business.title")}
            </h2>
            <p className={`mt-0.5 text-subheadline text-label-secondary`}>
              {t("kyc.business.subtitle")}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {status && <KycStatusBadge status={status} t={t} />}
            <button
              type="button"
              onClick={onClose}
              className={`rounded-radius-xs p-2 transition hover:bg-fill-quaternary`}
              aria-label={t("kyc.business.close")}
            >
              <FiX className={`h-5 w-5 text-label-secondary`} />
            </button>
          </div>
        </div>
        <div className="px-5 py-5 sm:px-6">
          <BusinessVerificationPanel
            isDark={isDark}
            t={t}
            mintSpaceId={mintSpaceId}
            setToast={setToast}
            onStatusChange={onStatusChange}
          />
        </div>
      </div>
    </div>
  );
}

/** The form in a window over the page — portalled, so no container can clip or offset it. */
export function BusinessVerificationModal({ open, ...props }) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(<Window {...props} />, document.body);
}
